/**
 * vendors.service.ts
 *
 * Typed API call wrappers for the Vendors domain.
 */
import { apiClient } from './apiClient';
import type { Product } from './inventory.service';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Vendor {
  vendorId: number;
  accountId: number;
  name: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVendorPayload {
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
}

export interface UpdateVendorPayload extends Partial<CreateVendorPayload> {
  active?: boolean;
}

export interface VendorListParams {
  search?: string;
  active?: boolean;
  page?: number;
  perPage?: number;
  export?: boolean;
}

export interface PaginatedVendors {
  data: Vendor[];
  meta: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
  };
}

// ─── API wrappers ─────────────────────────────────────────────────────────────

export async function listVendors(params?: VendorListParams): Promise<PaginatedVendors> {
  const res = await apiClient.get<PaginatedVendors>('/vendors', { params });
  return res.data;
}

export async function getVendor(vendorId: number): Promise<Vendor> {
  const res = await apiClient.get<{ data: Vendor }>(`/vendors/${vendorId}`);
  return res.data.data;
}

export async function createVendor(payload: CreateVendorPayload): Promise<Vendor> {
  const res = await apiClient.post<{ data: Vendor }>('/vendors', payload);
  return res.data.data;
}

export async function updateVendor(vendorId: number, payload: UpdateVendorPayload): Promise<Vendor> {
  const res = await apiClient.put<{ data: Vendor }>(`/vendors/${vendorId}`, payload);
  return res.data.data;
}

export async function deleteVendor(vendorId: number): Promise<void> {
  await apiClient.delete(`/vendors/${vendorId}`);
}

export async function listVendorProducts(vendorId: number): Promise<Product[]> {
  const res = await apiClient.get<{ data: Product[] }>(`/vendors/${vendorId}/products`);
  return res.data.data;
}
